import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { analyticsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  DollarSign, 
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Calendar,
  Package,
  BarChart3,
  PieChart,
  Target,
  Award,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Store,
  ShoppingBag,
  Building2,
  Globe,
  Percent,
  History,
  Star,
  ChevronRight,
  Minus
} from 'lucide-react';
import { HelpButton } from '../components/HelpButton';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatPercent = (value) => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

export const Analytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [seriesData, setSeriesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('all');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, seriesRes] = await Promise.all([
        analyticsAPI.getDashboard(),
        analyticsAPI.getSeriesPerformance()
      ]);
      setData(dashboardRes.data);
      setSeriesData(seriesRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F]"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-12 text-center shadow-hard">
            <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No Data Yet</h3>
            <p className="text-gray-600">Start recording sales and expenses to see your analytics</p>
          </div>
        </div>
      </div>
    );
  }

  const { summary, revenue_trend, top_events, worst_events, top_products_by_revenue, top_products_by_profit, payment_breakdown, expense_breakdown, channel_breakdown, forecast } = data;

  // Channel icons and colors mapping
  const channelConfig = {
    event: { icon: Store, color: 'bg-blue-500', label: 'Events' },
    etsy: { icon: ShoppingBag, color: 'bg-orange-500', label: 'Etsy' },
    boutique: { icon: Building2, color: 'bg-purple-500', label: 'Boutique' },
    website: { icon: Globe, color: 'bg-green-500', label: 'Website' },
    farmers_market: { icon: Store, color: 'bg-emerald-500', label: 'Farmers Market' },
    wholesale: { icon: Package, color: 'bg-indigo-500', label: 'Wholesale' },
    other: { icon: DollarSign, color: 'bg-gray-500', label: 'Other' },
  };

  const getChannelConfig = (channel) => channelConfig[channel] || channelConfig.other;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">Analytics Dashboard</h1>
              <p className="text-gray-600 mt-1">Track your business performance and insights</p>
            </div>
            <HelpButton tutorialId="analytics" />
          </div>
          <Button 
            onClick={() => navigate('/what-if')} 
            className="btn-primary"
            data-testid="what-if-btn"
          >
            <Target className="w-4 h-4 mr-2" />
            What-If Planner
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-5 shadow-hard">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Total Revenue</span>
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-[#1E3A5F]">{formatCurrency(summary.total_revenue)}</p>
            <p className="text-xs text-gray-500 mt-1">{summary.total_sales} sales</p>
          </div>
          
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-5 shadow-hard">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Net Profit</span>
              {summary.net_profit >= 0 ? (
                <TrendingUp className="w-5 h-5 text-green-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
            </div>
            <p className={`text-2xl font-bold ${summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(summary.net_profit)}
            </p>
            <p className={`text-xs mt-1 ${summary.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPercent(summary.profit_margin)} margin
            </p>
          </div>
          
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-5 shadow-hard">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Items Sold</span>
              <Package className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-[#1E3A5F]">{summary.total_items_sold}</p>
            <p className="text-xs text-gray-500 mt-1">{summary.events_attended} events</p>
          </div>
          
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-5 shadow-hard">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Avg per Event</span>
              <Calendar className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-[#1E3A5F]">{formatCurrency(summary.avg_revenue_per_event)}</p>
            <p className={`text-xs mt-1 ${summary.avg_profit_per_event >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(summary.avg_profit_per_event)} profit
            </p>
          </div>
        </div>

        {/* Revenue Trend Chart */}
        {revenue_trend.length > 0 && (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard mb-8">
            <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Revenue Trend
            </h2>
            <div className="h-64 flex items-end gap-2">
              {revenue_trend.map((month, idx) => {
                const maxRevenue = Math.max(...revenue_trend.map(m => m.revenue));
                const height = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
                const profitHeight = maxRevenue > 0 ? (Math.max(0, month.profit) / maxRevenue) * 100 : 0;
                return (
                  <div key={month.month} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex flex-col items-center" style={{ height: '200px' }}>
                      <div className="w-full flex justify-center items-end h-full gap-1">
                        <div 
                          className="w-1/2 bg-[#1E3A5F] rounded-t transition-all hover:bg-[#2a4a8c]"
                          style={{ height: `${height}%` }}
                          title={`Revenue: ${formatCurrency(month.revenue)}`}
                        />
                        <div 
                          className="w-1/2 bg-[#1E3A5F] rounded-t transition-all hover:bg-[#c99b1d]"
                          style={{ height: `${profitHeight}%` }}
                          title={`Profit: ${formatCurrency(month.profit)}`}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-left">
                      {month.month}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#1E3A5F] rounded" />
                <span className="text-sm text-gray-600">Revenue</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#1E3A5F] rounded" />
                <span className="text-sm text-gray-600">Profit</span>
              </div>
            </div>
          </div>
        )}

        {/* Forecast Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2a5a9c] rounded-xl p-5 text-white shadow-hard">
            <p className="text-sm opacity-80">30-Day Forecast</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(forecast['30_day'].revenue)}</p>
            <p className={`text-sm mt-1 ${forecast['30_day'].profit >= 0 ? 'text-green-300' : 'text-red-300'}`}>
              {formatCurrency(forecast['30_day'].profit)} projected profit
            </p>
          </div>
          <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2a5a9c] rounded-xl p-5 text-white shadow-hard">
            <p className="text-sm opacity-80">90-Day Forecast</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(forecast['90_day'].revenue)}</p>
            <p className={`text-sm mt-1 ${forecast['90_day'].profit >= 0 ? 'text-green-300' : 'text-red-300'}`}>
              {formatCurrency(forecast['90_day'].profit)} projected profit
            </p>
          </div>
          <div className="bg-gradient-to-br from-[#1E3A5F] to-[#c99b1d] rounded-xl p-5 text-white shadow-hard">
            <p className="text-sm opacity-80">Annual Forecast</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(forecast['365_day'].revenue)}</p>
            <p className={`text-sm mt-1 ${forecast['365_day'].profit >= 0 ? 'text-green-300' : 'text-red-300'}`}>
              {formatCurrency(forecast['365_day'].profit)} projected profit
            </p>
          </div>
        </div>

        {/* Sales Channel Breakdown */}
        {channel_breakdown && channel_breakdown.length > 0 && (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard mb-8">
            <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
              <Store className="w-5 h-5" />
              Sales by Channel
            </h2>
            
            {/* Channel Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
              {channel_breakdown.map((channel) => {
                const config = getChannelConfig(channel.channel);
                const ChannelIcon = config.icon;
                return (
                  <div 
                    key={channel.channel} 
                    className="p-4 rounded-xl border-2 border-gray-100 hover:border-[#1E3A5F] transition-colors"
                    data-testid={`channel-stat-${channel.channel}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <ChannelIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1E3A5F]">{channel.label}</p>
                        <p className="text-xs text-gray-500">{channel.sales_count} sales</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Revenue</span>
                        <span className="font-semibold text-green-600">{formatCurrency(channel.total_revenue)}</span>
                      </div>
                      {channel.total_fees > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 flex items-center gap-1">
                            <Percent className="w-3 h-3" /> Fees
                          </span>
                          <span className="text-red-500">-{formatCurrency(channel.total_fees)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm pt-1 border-t">
                        <span className="text-gray-500">Net Profit</span>
                        <span className={`font-semibold ${channel.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {formatCurrency(channel.net_profit)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Margin</span>
                        <span className={`${channel.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {channel.profit_margin.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Channel Revenue Comparison Bar Chart */}
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-600 mb-3">Revenue Comparison</h3>
              <div className="space-y-3">
                {channel_breakdown.map((channel) => {
                  const maxRevenue = Math.max(...channel_breakdown.map(c => c.total_revenue));
                  const percentage = maxRevenue > 0 ? (channel.total_revenue / maxRevenue) * 100 : 0;
                  const config = getChannelConfig(channel.channel);
                  const ChannelIcon = config.icon;
                  return (
                    <div key={channel.channel} className="flex items-center gap-3">
                      <div className="w-24 flex items-center gap-2">
                        <ChannelIcon className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium truncate">{channel.label}</span>
                      </div>
                      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
                        <div 
                          className={`h-full ${config.color} rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                        <span className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-mono">
                          {formatCurrency(channel.total_revenue)}
                        </span>
                      </div>
                      <div className="w-20 text-right">
                        <span className={`text-xs font-medium ${channel.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(channel.net_profit)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Top Performing Events */}
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard">
            <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-[#1E3A5F]" />
              Top Performing Events
            </h2>
            {top_events.length > 0 ? (
              <div className="space-y-3">
                {top_events.slice(0, 5).map((event, idx) => (
                  <div key={event.event_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      idx === 0 ? 'bg-[#1E3A5F]' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1E3A5F] truncate">{event.event?.title || 'Unknown Event'}</p>
                      <p className="text-xs text-gray-500">{event.items_sold} items sold</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{formatCurrency(event.net_profit)}</p>
                      <p className="text-xs text-gray-500">{formatCurrency(event.total_revenue)} rev</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No event data yet</p>
            )}
          </div>

          {/* Top Products */}
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard">
            <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-500" />
              Top Products by Revenue
            </h2>
            {top_products_by_revenue.length > 0 ? (
              <div className="space-y-3">
                {top_products_by_revenue.slice(0, 5).map((product, idx) => (
                  <div key={product.product_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      idx === 0 ? 'bg-purple-500' : idx === 1 ? 'bg-purple-400' : idx === 2 ? 'bg-purple-300' : 'bg-gray-300'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1E3A5F] truncate">{product.product_name}</p>
                      <p className="text-xs text-gray-500">{product.quantity_sold} units @ {formatCurrency(product.avg_price)} avg</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#1E3A5F]">{formatCurrency(product.total_revenue)}</p>
                      <p className="text-xs text-green-600">{formatCurrency(product.gross_profit)} profit</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No product data yet</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Payment Methods */}
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard">
            <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-500" />
              Payment Methods
            </h2>
            {Object.keys(payment_breakdown).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(payment_breakdown).sort((a, b) => b[1] - a[1]).map(([method, amount]) => {
                  const total = Object.values(payment_breakdown).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? (amount / total) * 100 : 0;
                  return (
                    <div key={method} className="flex items-center gap-3">
                      <span className="capitalize font-medium w-20">{method}</span>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#1E3A5F] rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="font-mono text-sm w-24 text-right">{formatCurrency(amount)}</span>
                      <span className="text-xs text-gray-500 w-12 text-right">{percentage.toFixed(0)}%</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No payment data yet</p>
            )}
          </div>

          {/* Expense Breakdown */}
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard">
            <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-red-500" />
              Expense Breakdown
            </h2>
            {Object.keys(expense_breakdown).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(expense_breakdown).sort((a, b) => b[1] - a[1]).map(([category, amount]) => {
                  const total = Object.values(expense_breakdown).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? (amount / total) * 100 : 0;
                  const labels = {
                    booth_fee: 'Booth Fee',
                    travel: 'Travel',
                    lodging: 'Lodging',
                    supplies: 'Supplies',
                    marketing: 'Marketing',
                    equipment: 'Equipment',
                    insurance: 'Insurance',
                    other: 'Other'
                  };
                  return (
                    <div key={category} className="flex items-center gap-3">
                      <span className="font-medium w-24 truncate">{labels[category] || category}</span>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-red-500 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="font-mono text-sm w-24 text-right">{formatCurrency(amount)}</span>
                      <span className="text-xs text-gray-500 w-12 text-right">{percentage.toFixed(0)}%</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No expense data yet</p>
            )}
          </div>
        </div>

        {/* Series Performance Section */}
        {seriesData && seriesData.series && seriesData.series.length > 0 && (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-[#1E3A5F] flex items-center gap-2">
                <History className="w-5 h-5 text-[#1E3A5F]" />
                Event Series Performance
              </h2>
              <div className="text-sm text-gray-500">
                {seriesData.total_series} series tracked
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-[#1E3A5F]/5 rounded-xl">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1E3A5F]">{formatCurrency(seriesData.summary.total_series_sales)}</p>
                <p className="text-xs text-gray-500">Total Series Sales</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1E3A5F]">
                  {seriesData.summary.avg_series_rating ? (
                    <span className="flex items-center justify-center gap-1">
                      {seriesData.summary.avg_series_rating}
                      <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    </span>
                  ) : '—'}
                </p>
                <p className="text-xs text-gray-500">Avg Rating</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-[#1E3A5F]">
                  {seriesData.summary.most_profitable_series?.name || '—'}
                </p>
                <p className="text-xs text-gray-500">Most Profitable</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-[#1E3A5F]">
                  {seriesData.summary.most_attended_series?.name || '—'}
                </p>
                <p className="text-xs text-gray-500">Most Attended</p>
              </div>
            </div>

            {/* Series List */}
            <div className="space-y-4">
              {seriesData.series.map((series) => (
                <div key={series.series_id} className="border border-gray-200 rounded-xl p-4 hover:border-[#1E3A5F] transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-[#1E3A5F]">{series.name}</h3>
                      <p className="text-sm text-gray-500">
                        {series.occurrence_count} year{series.occurrence_count !== 1 ? 's' : ''} attended
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-[#1E3A5F]">{formatCurrency(series.total_revenue)}</p>
                      <p className="text-sm text-gray-500">total revenue</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="font-semibold text-gray-700">{formatCurrency(series.avg_revenue_per_occurrence)}</p>
                      <p className="text-xs text-gray-500">Avg/Year</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="font-semibold text-gray-700">{formatCurrency(series.total_booth_costs)}</p>
                      <p className="text-xs text-gray-500">Booth Costs</p>
                    </div>
                    <div className={`rounded-lg p-2 text-center ${series.net_profit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className={`font-semibold ${series.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(series.net_profit)}
                      </p>
                      <p className="text-xs text-gray-500">Net Profit</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="font-semibold text-gray-700">{series.total_items_sold}</p>
                      <p className="text-xs text-gray-500">Items Sold</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      {series.avg_rating ? (
                        <p className="font-semibold text-gray-700 flex items-center justify-center gap-1">
                          {series.avg_rating}
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        </p>
                      ) : (
                        <p className="font-semibold text-gray-400">—</p>
                      )}
                      <p className="text-xs text-gray-500">Rating</p>
                    </div>
                  </div>

                  {/* Occurrences breakdown */}
                  {series.occurrences && series.occurrences.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 mb-2">Year-over-Year:</p>
                      <div className="flex flex-wrap gap-2">
                        {series.occurrences.map((occ) => (
                          <Link
                            key={occ.event_id}
                            to={`/events/${occ.event_id}`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1E3A5F]/10 hover:bg-[#1E3A5F]/20 rounded-lg text-sm transition-colors"
                          >
                            <Calendar className="w-3 h-3 text-[#1E3A5F]" />
                            <span className="text-[#1E3A5F] font-medium">
                              {occ.date ? new Date(occ.date).getFullYear() : 'N/A'}
                            </span>
                            <span className="text-gray-600">{formatCurrency(occ.revenue)}</span>
                            {occ.roi !== null && (
                              <span className={`text-xs ${occ.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                ({occ.roi > 0 ? '+' : ''}{occ.roi}% ROI)
                              </span>
                            )}
                            {occ.rating && (
                              <span className="flex items-center text-yellow-600">
                                {occ.rating}<Star className="w-3 h-3 fill-yellow-500" />
                              </span>
                            )}
                            <ChevronRight className="w-3 h-3 text-gray-400" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Underperforming Events Alert */}
        {worst_events.length > 0 && worst_events[0].net_profit < 0 && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6 shadow-hard">
            <h2 className="text-lg font-semibold text-amber-700 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Events to Review
            </h2>
            <p className="text-sm text-amber-600 mb-4">
              These events had negative returns. Consider adjusting pricing, reducing expenses, or reconsidering attendance.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {worst_events.filter(e => e.net_profit < 0).slice(0, 3).map((event) => (
                <div key={event.event_id} className="bg-white p-4 rounded-lg border border-amber-200">
                  <p className="font-medium text-[#1E3A5F] truncate">{event.event?.title || 'Unknown Event'}</p>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className="text-gray-500">Revenue:</span>
                    <span>{formatCurrency(event.total_revenue)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Expenses:</span>
                    <span>{formatCurrency(event.total_expenses)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium mt-1 pt-1 border-t">
                    <span className="text-red-600">Loss:</span>
                    <span className="text-red-600">{formatCurrency(event.net_profit)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
